import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { useAutoRefetch } from '@/lib/useAutoRefetch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { fmtDateTime } from '@/lib/format';
import { Archive, FileText, ShieldCheck, ClipboardCheck, MapPin, Users2, Search, RotateCcw, ExternalLink } from 'lucide-react';

// Distinct heading + subtext to differentiate from Weekly Operating Rhythm archive.
const HEADING = 'Archived Records';
const SUB = 'Restore or review previously-archived Work Orders, Master Projects, Quotes, Trips & Visits, and Team members. This is not the Weekly Operating Rhythm archive.';

const TABS = [
  { id: 'work_orders',     label: 'Work Orders',       icon: FileText,        endpoint: '/archives/work-orders',     restore: (id) => `/work_orders/${id}/unarchive` },
  { id: 'master_projects', label: 'Master Projects',   icon: ClipboardCheck,  endpoint: '/archives/master-projects', restore: (id) => `/master-projects/${id}/unarchive` },
  { id: 'quotes',          label: 'Quotes',            icon: ShieldCheck,     endpoint: '/archives/quotes',          restore: null },
  { id: 'visits',          label: 'Trips & Visits',    icon: MapPin,          endpoint: '/archives/visits',          restore: null },
  { id: 'users',           label: 'Team Directory',    icon: Users2,          endpoint: '/archives/users',           restore: (id) => `/admin/users/${id}/unarchive` },
];

export default function ArchivesPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [tab, setTab] = useState('work_orders');
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [summary, setSummary] = useState({});
  const [busy, setBusy] = useState(false);

  const meta = TABS.find(t => t.id === tab);

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get('/archives/summary');
      setSummary(r.data || {});
    } catch (e) { toast.error(parseErr(e)); }
  }, []);

  const loadRows = useCallback(async () => {
    if (!meta) return;
    // Hide the Users tab from non-Owner users.
    if (meta.id === 'users' && !isOwner) {
      setRows([]);
      return;
    }
    setBusy(true);
    try {
      const url = q ? `${meta.endpoint}?q=${encodeURIComponent(q)}` : meta.endpoint;
      const r = await api.get(url);
      setRows(r.data?.rows || []);
    } catch (e) { toast.error(parseErr(e)); setRows([]); }
    finally { setBusy(false); }
  }, [meta, q, isOwner]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useAutoRefetch(loadRows, {
    collections: ['work_orders', 'master_projects', 'quotes', 'visits', 'users'],
  });

  const restore = async (row) => {
    if (!meta.restore) return;
    if (!confirm('Restore this archived record? Linked archived children (quotes, trips, follow-ups) will be restored too where applicable.')) return;
    try {
      await api.post(meta.restore(row.id));
      toast.success('Restored');
      loadSummary(); loadRows();
    } catch (e) { toast.error(parseErr(e)); }
  };

  const visibleTabs = TABS.filter(t => t.id !== 'users' || isOwner);

  return (
    <AppShell breadcrumbs={[{ label: HEADING }]}>
      <div className="space-y-4" data-testid="archives-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Archive size={20} className="text-[hsl(var(--primary))]" />
              <h1 className="text-[22px] font-semibold" data-testid="archives-title">{HEADING}</h1>
            </div>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-0.5 max-w-[720px]">
              {SUB}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 border-b pb-2" style={{ borderColor: 'hsl(var(--border))' }} data-testid="archives-tabs">
          {visibleTabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            const count = summary[t.id] ?? 0;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                      data-testid={`archives-tab-${t.id}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] border transition-colors ${
                        active
                          ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]'
                          : 'border-[hsl(var(--border))] text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--surface-1))]'
                      }`}>
                <Icon size={13} /> {t.label}
                <span className={`text-[10px] px-1.5 py-[1px] rounded-full ${active ? 'bg-white/20' : 'bg-[hsl(var(--surface-1))]'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-[400px]">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--foreground-subtle))]" />
            <Input value={q} onChange={e => setQ(e.target.value)}
                   placeholder="Search by name, client, WO number, code…"
                   className="pl-8" data-testid="archives-search" />
          </div>
          <span className="text-[11.5px] text-[hsl(var(--foreground-muted))]">{rows.length} archived record{rows.length === 1 ? '' : 's'}</span>
        </div>

        {busy && <div className="py-8 text-center text-[hsl(var(--foreground-muted))]">Loading…</div>}

        {!busy && rows.length === 0 && (
          <div className="border rounded-xl p-8 text-center text-[hsl(var(--foreground-muted))]"
               style={{ borderColor: 'hsl(var(--border))' }}
               data-testid={`archives-empty-${tab}`}>
            No archived {meta.label.toLowerCase()} to show.
          </div>
        )}

        {!busy && rows.length > 0 && (
          <div className="border rounded-xl bg-[hsl(var(--card))] overflow-x-auto"
               style={{ borderColor: 'hsl(var(--border))' }}
               data-testid={`archives-table-${tab}`}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name / ID</th>
                  {tab !== 'users' && <th>Client / Context</th>}
                  <th>Archived by</th>
                  <th>Archived at</th>
                  <th>Reason</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} data-testid={`archives-row-${r.id}`}>
                    <td className="font-medium">
                      {rowTitle(tab, r)}
                    </td>
                    {tab !== 'users' && (
                      <td className="text-[12.5px] text-[hsl(var(--foreground-muted))]">
                        {rowContext(tab, r)}
                      </td>
                    )}
                    <td className="text-[11.5px] font-mono text-[hsl(var(--foreground-muted))]">
                      {r.meta?.archived_by || '—'}
                    </td>
                    <td className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
                      {r.meta?.archived_at ? fmtDateTime(r.meta.archived_at) : '—'}
                    </td>
                    <td className="text-[11.5px] max-w-[220px] truncate">
                      <span className="chip">{r.meta?.archived_reason || 'archived_by_user'}</span>
                    </td>
                    <td className="text-right whitespace-nowrap space-x-1">
                      {openLink(tab, r) && (
                        <Link to={openLink(tab, r)}
                              className="inline-flex items-center gap-1 text-[11.5px] text-[hsl(var(--primary))] hover:underline"
                              data-testid={`archives-open-${r.id}`}>
                          <ExternalLink size={11} /> Open
                        </Link>
                      )}
                      {meta.restore && (
                        <Button size="sm" variant="outline" onClick={() => restore(r)}
                                data-testid={`archives-restore-${r.id}`}>
                          <RotateCcw size={11} className="mr-1" /> Restore
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function rowTitle(tab, r) {
  if (tab === 'work_orders')     return r.wo_number || r.extraction?.wo_number || r.id?.slice(0, 8);
  if (tab === 'master_projects') return r.project_name || r.name || r.code || r.id?.slice(0, 8);
  if (tab === 'quotes')          return r.quote_number || r.name || r.id?.slice(0, 8);
  if (tab === 'visits')          return r.trip_purpose || r.destination || r.id?.slice(0, 8);
  if (tab === 'users')           return r.name || r.email || r.id?.slice(0, 8);
  return r.id?.slice(0, 8) || '—';
}

function rowContext(tab, r) {
  if (tab === 'work_orders')     return r.client_name || r.extraction?.client_name || '';
  if (tab === 'master_projects') return `${r.client || ''} · ${r.code || ''}`;
  if (tab === 'quotes')          return r.linked_project_id ? `Project ${r.linked_project_id.slice(0, 8)}` : '';
  if (tab === 'visits')          return r.project_name || (r.project_id ? `Project ${r.project_id.slice(0, 8)}` : '');
  return '';
}

function openLink(tab, r) {
  if (tab === 'work_orders')     return `/work-orders/${r.id}/verify`;
  if (tab === 'master_projects') return `/projects/${r.id}`;
  return null;
}
